// THE MERGE GATE's client (#6779) over #7011's two routes: `GET|PUT
// /v1/repos/:owner/:repo/merge-override` (`apps/api/src/routes/repos-merge-override.ts`).
// The hold is per repo (owner ruling V8), stored platform-side (V11), and only tightens
// (G-7): `{ auto_merge: false }` holds any repo, `{ auto_merge: true }` clears a hold and
// is refused with a 409 `override_cannot_loosen` where the repo would not auto-merge
// without it.
//
// THE WIRE (`mergeOverrideWire`, `packages/mcp/src/army/project-merge-override.ts`, which
// the dashboard may not import, so the schema is spelled here and parsed at the door):
//   held:     `{ auto_merge: false, file_allows, set_by: { user_id, login } | null, set_at }`
//   not held: `{ auto_merge: null, file_allows }`
// `file_allows` is the EFFECTIVE `pr.auto_merge` after the tier gate and the defaults, false
// also for a repo with no or an invalid snapshot, or a free plan's repo that never opted in;
// the GET carries no reason, so no copy here names the file as the cause.
// (`packages/domain`'s `MergeOverrideWireSchema` is the policy read's field, another shape.)
//
// THE SERVER IS THE ARBITER: the PUT is `write:merge:gate`, which `ADMIN_SCOPES` carries, so
// `canAdminWrite` decides whether to offer the button and the route decides the write. A
// refusal (a 403 for a credential without the scope, the 409) renders the route's `detail`
// and changes nothing. A 200's body replaces the state, then the GET is re-read either way
// (`apps/dashboard/CLAUDE.md` § Boundary). A 404 (a repo not enabled for this account)
// offers no control at all.

import { createQuery, useQueryClient } from '@tanstack/solid-query';
import { z } from 'zod';
import { t } from '../i18n';
import { activeAccountId } from './active-account';
import { ApiError, apiFetch } from './api';
import { createMutation, type Mutation } from './mutation';
import { absolute } from './time';

export const MergeOverrideSchema = z.union([
  z.object({
    auto_merge: z.literal(false),
    file_allows: z.boolean(),
    set_by: z.object({ user_id: z.string(), login: z.string().nullable() }).nullable(),
    set_at: z.string(),
  }),
  z.object({ auto_merge: z.null(), file_allows: z.boolean() }),
]);

export type MergeOverride = z.infer<typeof MergeOverrideSchema>;

export const MERGE_OVERRIDE_QUERY_KEY = 'merge_override';

/** `/v1/repos/<owner>/<repo>/merge-override`, each segment encoded. */
export function mergeOverridePath(repo: string): string {
  const [owner = '', name = ''] = repo.split('/');
  return `/v1/repos/${encodeURIComponent(owner)}/${encodeURIComponent(name)}/merge-override`;
}

/**
 * What the button shows and does:
 *   - `autonomous`: not held, the repo auto-merges: "Autonomous", clicking holds it;
 *   - `held`: held, the repo would auto-merge without it: "Held for review", clicking clears it;
 *   - `hold`: not held, the repo does not auto-merge on its own: "Hold for review", clicking
 *     holds it (holding is always available, ruling G-7);
 *   - `locked`: held, and the repo would not auto-merge without it: "Held for review",
 *     disabled ("Autonomous" is never offered where `file_allows` is false).
 */
export type GateState = 'autonomous' | 'held' | 'hold' | 'locked';

export function gateState(o: MergeOverride): GateState {
  if (o.auto_merge === false) return o.file_allows ? 'held' : 'locked';
  return o.file_allows ? 'autonomous' : 'hold';
}

/** The PUT body's `auto_merge` a click sends, or `null` where a click sends nothing. */
export function gateWrite(state: GateState): boolean | null {
  if (state === 'locked') return null;
  return state === 'held';
}

/** Who holds it: the login, else the user id, else a token-only caller (MCP's `mergeHoldLine`). */
export function holdWho(o: Extract<MergeOverride, { auto_merge: false }>): string {
  return o.set_by?.login ?? o.set_by?.user_id ?? t('fleet.live.gate_token_caller');
}

/** The code block's lines: the resolved value, and the hold when there is one. */
export function gateCode(o: MergeOverride, repo: string): readonly string[] {
  const head = t('fleet.live.gate_code_head', { repo });
  if (o.auto_merge === false) {
    return [head, t('fleet.live.gate_code_held', { who: holdWho(o), at: absolute(o.set_at) })];
  }
  return [head, o.file_allows ? t('fleet.live.gate_code_on') : t('fleet.live.gate_code_off')];
}

export type MergeGate = {
  /** The subject repo's state; `undefined` while it loads, or when the read failed. */
  readonly state: () => MergeOverride | undefined;
  /** The read answered 404: no button and no badge for that repo. */
  readonly notFound: () => boolean;
  /** The read failed for another reason. */
  readonly unread: () => boolean;
  /** The click: one PUT for the state shown, never two in flight. */
  readonly save: Mutation<[]>;
};

/**
 * The Merge Gate for one subject repo: the GET on mount and each time `repo()` changes,
 * and the PUT. `notify` shows a refusal's `detail` (the page's toast).
 */
export function createMergeGate(
  token: string | null,
  repo: () => string | null,
  notify: (message: string, tone: 'error') => void,
): MergeGate {
  const client = useQueryClient();
  const key = (r: string | null) => [MERGE_OVERRIDE_QUERY_KEY, token, activeAccountId(), r];
  const query = createQuery<MergeOverride>(() => {
    const r = repo();
    return {
      queryKey: key(r),
      enabled: token !== null && r !== null,
      retry: (n: number, err: unknown) => !(err instanceof ApiError && err.status === 404) && n < 2,
      queryFn: () =>
        apiFetch(mergeOverridePath(r ?? ''), token, undefined, { schema: MergeOverrideSchema }),
    };
  });
  const state = (): MergeOverride | undefined => (query.isError ? undefined : query.data);
  // THE KEY IS CAPTURED BEFORE THE AWAIT (review thread 4407263583). It carries the active
  // account, so reading it after the PUT answered named whichever account was active THEN:
  // a switch while the PUT was in flight wrote one account's hold into another's entry.
  // The answer, and the re-read after it, belong to the account the PUT was sent under.
  let sent: ReturnType<typeof key> | null = null;
  const save = createMutation<[], MergeOverride>({
    fallback: t('fleet.live.gate_failed'),
    call: async () => {
      const r = repo();
      const s = state();
      const write = s === undefined ? null : gateWrite(gateState(s));
      if (r === null || write === null) throw new Error('nothing to send');
      const k = key(r);
      sent = k;
      const body = await apiFetch(
        mergeOverridePath(r),
        token,
        { method: 'PUT', body: JSON.stringify({ auto_merge: write }) },
        { schema: MergeOverrideSchema },
      );
      client.setQueryData(k, body);
      return body;
    },
    onError: (message) => notify(message, 'error'),
    onSettled: () => {
      const k = sent;
      sent = null;
      if (k === null) return undefined;
      // Still the account (and repo) on screen: re-read it. Otherwise the entry the PUT
      // wrote is marked stale for that account's next visit, and the screen's own entry,
      // which this PUT never touched, is left alone.
      if (JSON.stringify(k) === JSON.stringify(key(repo()))) return query.refetch();
      return client.invalidateQueries({ queryKey: k, exact: true, refetchType: 'none' });
    },
  });
  return {
    state,
    notFound: () => query.error instanceof ApiError && query.error.status === 404,
    unread: () => query.isError && !(query.error instanceof ApiError && query.error.status === 404),
    save,
  };
}
